import React, { useState } from 'react';
import { AlertCircle } from 'lucide-react';

interface TextareaProps extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'placeholder'> {
  label: string;
  error?: string;
  /** Contador de caracteres abaixo (útil pra campos com mínimo/máximo). */
  showCharCount?: boolean;
  maxLength?: number;
  /** Mínimo exibido no contador — só rótulo, validação é do consumidor. */
  minLength?: number;
}

/**
 * Textarea com o mesmo visual do TextInput compartilhado — label
 * flutuante, `rounded-lg`, borda cinza→azul→vermelho e mensagem de
 * erro abaixo com ícone.
 *
 * Diferente do TextInput: altura cresce com `rows` (default 3) e o
 * label começa no topo (não centralizado) porque textarea multi-linha
 * fica estranho com label centralizada verticalmente quando vazia.
 */
export const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      label,
      error,
      showCharCount,
      maxLength,
      minLength,
      disabled,
      onFocus,
      onBlur,
      className,
      rows = 3,
      ...props
    },
    ref,
  ) => {
    const [isFocused, setIsFocused] = useState(false);
    const generatedId = React.useId();
    const inputId = props.id ?? generatedId;

    const rawValue = props.value ?? props.defaultValue;
    const hasValue = rawValue !== undefined && rawValue !== '' && rawValue !== null;
    const isFloated = isFocused || hasValue;

    const borderColor = error
      ? 'border-red-500'
      : isFocused
      ? 'border-blue-500'
      : 'border-gray-300';

    const currentLength = typeof rawValue === 'string' ? rawValue.length : 0;

    return (
      <div className="w-full">
        <div className={`relative ${disabled ? 'opacity-60' : ''}`}>
          <textarea
            ref={ref}
            {...props}
            id={inputId}
            rows={rows}
            placeholder=""
            disabled={disabled}
            maxLength={maxLength}
            onFocus={(e) => {
              setIsFocused(true);
              onFocus?.(e);
            }}
            onBlur={(e) => {
              setIsFocused(false);
              onBlur?.(e);
            }}
            className={[
              'peer w-full rounded-lg border bg-white px-4 py-3 text-sm',
              'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-0',
              'resize-y',
              borderColor,
              disabled ? 'bg-gray-50 cursor-not-allowed' : '',
              className ?? '',
            ].join(' ')}
          />

          <label
            htmlFor={inputId}
            className={[
              'absolute pointer-events-none transition-all duration-200 select-none',
              isFloated
                ? `left-3 -top-2.5 text-xs bg-white px-1 font-medium ${
                    error ? 'text-red-500' : isFocused ? 'text-blue-600' : 'text-gray-500'
                  }`
                : 'left-4 top-3 text-sm text-gray-400',
            ].join(' ')}
          >
            {label}
          </label>
        </div>

        <div className="mt-1 flex items-start justify-between gap-2">
          {error ? (
            <p className="text-sm text-red-600 flex items-center gap-1">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {error}
            </p>
          ) : (
            <span />
          )}
          {showCharCount && (
            <span
              className={`text-xs ${
                minLength && currentLength < minLength ? 'text-amber-600' : 'text-gray-500'
              }`}
            >
              {currentLength}
              {minLength ? `/${minLength}` : ''}
              {maxLength ? ` (máx ${maxLength})` : ''} caracteres
            </span>
          )}
        </div>
      </div>
    );
  },
);

Textarea.displayName = 'Textarea';
